import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, OnInit, resource, signal } from '@angular/core';
import { Sparte, TextNodeType } from '@mwe/models';
import { isProductCategory, translateCategory } from '@mwe/utils';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { HeadlineService } from '../headline/headline.service';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../headline/headline.component';
import { CategoryIconSrcPipe } from '@mwe/ui/pipes';
import { ProfileService } from '@mwe/services';
import { FeatureToggleEnum, ProductCategoryEnum } from '@mwe/constants';

type ProductIconsSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

@Component({
  selector: 'mwe-product-icons',
  templateUrl: './product-icons.component.html',
  imports: [NgClass, HeadlineComponent, CategoryIconSrcPipe],
  providers: [HeadlineService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductIconsComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly headlineService = inject(HeadlineService);
  private readonly profileService = inject(ProfileService);

  readonly categories = input<(Sparte | string)[]>([]);
  readonly size = input<ProductIconsSize>('md');
  readonly textNodeType = input<TextNodeType>('h6');
  readonly isDisabled = input<boolean>(false);
  readonly alignment = input<'start' | 'center' | 'end'>('start');
  readonly showHeadline = input<boolean>(true);

  readonly categoryTranslationKeys = model<Record<string, string>>({});

  private readonly isNewInternetProcessEnabled = signal(false); // TODO: Only for internet product card (until internet-v2-enabled is enabled)
  private readonly categoriesInternal = signal<string[]>([], {
    equal: (a, b) => a?.length === b?.length && a.every((value, i) => value === b[i]),
  });

  readonly joinedLabels = computed<string>(() => this.translatedProductCategoriesResource.value().join(' & '));
  readonly maxWidth = computed<string>(() => this.getMaxWidth());
  readonly fontSize = computed<string>(() => this.getFontSize());
  readonly rowClass = computed<string>(() => this.getRowClass());
  readonly textClass = computed<string>(() => this.getTextClass());

  private readonly translatedProductCategoriesResource = resource({
    defaultValue: [] as string[],
    params: () => ({ categories: this.categoriesInternal(), categoryTranslationKeys: this.categoryTranslationKeys() }),
    loader: async ({ params }) => {
      const categories = params.categories;
      const categoryTranslationKeys = params.categoryTranslationKeys;
      if (!categories || !categoryTranslationKeys) return [];

      return Promise.all(
        categories.map(category => {
          const translationKey = categoryTranslationKeys[category];
          if (translationKey) return firstValueFrom(this.translateService.get(translationKey));

          return translateCategory(category, this.translateService);
        }),
      );
    },
  });

  constructor() {
    effect(() => this.categoriesInternal.set(this.categories()));

    // TODO: Only for internet product card (until internet-v2-enabled is enabled)
    effect(() => {
      const isNewInternetProcessEnabled = this.isNewInternetProcessEnabled();
      const hasInternetProduct = this.categories()?.some(category => isProductCategory(category, ProductCategoryEnum.INTERNET));
      if (!isNewInternetProcessEnabled || !hasInternetProduct) return;

      // given key value pairs should always be the higher priority.
      this.categoryTranslationKeys.update(translations => ({
        [Sparte.Internet]: 'products.category.internet_new',
        ...translations,
      }));
    });
  }

  async ngOnInit(): Promise<void> {
    this.headlineService.setReferenceHeadline(this.textNodeType());
    this.isNewInternetProcessEnabled.set(this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.INTERNET_V2_ENABLED));
  }

  private getMaxWidth(): string {
    switch (this.size()) {
      case '2xs': {
        return '.75rem';
      }
      case 'xs': {
        return '1rem';
      }
      case 'sm': {
        return '1.25rem';
      }
      case 'md': {
        return '1.5rem';
      }
      case 'lg': {
        return '2rem';
      }
      case 'xl': {
        return '2.5rem';
      }
      case '2xl': {
        return '3.5rem';
      }
      case '3xl': {
        return '4rem';
      }
      case '4xl': {
        return '4.5rem';
      }
    }
  }

  private getFontSize(): string {
    switch (this.size()) {
      case '2xs': {
        return '.75rem';
      }
      case 'xs': {
        return '.75rem';
      }
      case 'sm': {
        return '.75rem';
      }
      case 'md': {
        return '1.125rem';
      }
      case 'lg': {
        return '1.125rem';
      }
      case 'xl': {
        return '1.125rem';
      }
      case '2xl': {
        return '1.125rem';
      }
      case '3xl': {
        return '1.125rem';
      }
      case '4xl': {
        return '1.125rem';
      }
    }
  }

  private getRowClass(): string {
    switch (this.alignment()) {
      case 'start': {
        return 'justify-content-start';
      }
      case 'center': {
        return 'justify-content-center';
      }
      case 'end': {
        return 'justify-content-end';
      }
    }
  }

  private getTextClass(): string {
    switch (this.alignment()) {
      case 'start': {
        return 'text-start';
      }
      case 'center': {
        return 'text-center';
      }
      case 'end': {
        return 'text-end';
      }
    }
  }
}
